import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { InputFields, FieldType } from "@/components/Common/form/InputFields";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { useStore } from "@/stores/useStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment, useCallback, useMemo } from "react";
import * as React from "react";
import { useForm, useFormContext } from "react-hook-form";
import { ZodObject, ZodRawShape, z } from "zod";
import {
  CARD_FIELDS,
  CardFieldKeys,
  PatchCardDto,
  CreateCardDto,
  Card,
  Customer,
  TerminologyOptions,
  ManualCardContents,
  CardFieldKey,
  VEHICLE_FIELDS,
} from "@/types/v2";
import { getServiceAdvisorColor } from "@/lib/employees";
import { cn, firestoreToDateTime, formatPhoneNumber } from "@/lib/utils";
import { TagsField } from "@/components/Common/form/TagsField";
import { sortTagsByShopOrder } from "@/lib/tags";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { SwitchField } from "@/components/Common/form/SwitchField";
import { SHOW_ALL_SUGGESTED_TAGS } from "@/lib/const";
import { useTechnicians } from "@/hooks/shop/useTechnicians";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import shortUUID from "short-uuid";
import {
  migrateCardFieldData,
  resolveHiddenLegacyFields,
} from "@/lib/fieldMigration";
import { useCardNumberAutoIncrement } from "@/hooks/useCardNumberAutoIncrement";
import { FieldSectionManager, FieldSection } from "./FieldSectionManager";
import { getCardContents } from "@/lib/cards";

// The exact shape the phone Input's own onChange formats digits into (see
// the "customer.phone" FormField below) -- shared with
// `normalizePhoneForForm` so a source card's raw phone data can be coerced
// into something this regex actually accepts.
const PHONE_NUMBER_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;
const PHONE_TYPES = ["mobile", "home", "work"] as const;

// This form's phone Input has no extension field of its own (its onChange
// never produces one), so an extension formatPhoneNumber appended is
// dropped rather than kept -- there's nowhere in this UI to put it.
const PHONE_EXTENSION_SUFFIX = / ext\. \d+$/;

/**
 * Coerces a source card's phone entries (from `cardData`/`initialValues`,
 * e.g. an integration-synced customer whose phone was never run through
 * this form) into the shape `formSchema` and the phone Select actually
 * accept: the number formatted to `(XXX) XXX-XXXX` (dropping any extension
 * suffix `formatPhoneNumber` appends, since this form has nowhere to put
 * one), and `type` narrowed to one of the Select's three options (falling
 * back to "mobile", mirroring the phone field's own fallback for a missing
 * type). An entry that still can't be normalized into the canonical
 * 10-digit shape (too few/many digits) is dropped rather than left
 * invalid, so the form opens valid -- and duplicating a Tekmetric card
 * stays a two-click save -- for every case except one that couldn't be
 * saved before either.
 */
function normalizePhoneForForm(
  phone: Customer["phone"] | undefined
): NonNullable<Customer["phone"]> {
  if (!phone?.length) return [];
  return phone.reduce<NonNullable<Customer["phone"]>>((normalized, entry) => {
    const formattedNumber = formatPhoneNumber(entry.number ?? "").replace(
      PHONE_EXTENSION_SUFFIX,
      ""
    );
    if (!PHONE_NUMBER_REGEX.test(formattedNumber)) return normalized;
    const lowerType = entry.type?.toLowerCase();
    const type = (PHONE_TYPES as readonly string[]).includes(lowerType ?? "")
      ? (lowerType as (typeof PHONE_TYPES)[number])
      : "mobile";
    normalized.push({ ...entry, number: formattedNumber, type });
    return normalized;
  }, []);
}

/**
 * `customer.phone` is bound at the array level (the phone row below writes
 * the whole array via field.onChange), but the zod regex failure lands on
 * the nested `customer.phone.0.number` path -- `<FormMessage />` reads
 * `errors.customer.phone.message`, which is undefined on that array-shaped
 * error object, rendering the literal string "undefined" instead of the
 * real message. Read the nested leaf directly instead.
 *
 * A real component (not inline logic in the phone field's render prop) so
 * `useFormField()` can be called correctly -- it needs to run as a
 * descendant of `<FormItem>`'s context provider to resolve the same
 * `formMessageId` `FormControl` already wires into that field's
 * `aria-describedby`.
 *
 * (Cast: `formSchema` is annotated as `ZodObject<typeof shape>`, which
 * erases the nested `customer.phone` error shape react-hook-form actually
 * produces.)
 */
function PhoneNumberErrorMessage() {
  const { formMessageId } = useFormField();
  const { formState } = useFormContext();
  const phoneNumberError = (
    formState.errors.customer as
      | { phone?: { number?: { message?: string } }[] }
      | undefined
  )?.phone?.[0]?.number;

  if (!phoneNumberError) return null;

  return (
    <p id={formMessageId} className="text-sm font-medium text-destructive">
      {phoneNumberError.message}
    </p>
  );
}

const shape: ZodRawShape = {};
CARD_FIELDS.forEach((field) => {
  shape[field.key] = z.string().optional();
});
const formSchema: ZodObject<typeof shape> = z.object({
  cardNumber: z.string().min(1, {
    message: "Repair Order Number must exist",
  }),
  keyTag: z.string().optional(),
  source: z.string().readonly(),
  schemaVersion: z.number().int(),
  serviceWriterId: z.string().optional(),
  technicianId: z.string().optional(),
  totalLaborHours: z
    .object({
      estimatedLabor: z.coerce.number(),
      loggedLabor: z.coerce.number(),
    })
    .optional(),
  promisedTime: z.date().nullable().optional(),
  dropOffTime: z.date().nullable().optional(),
  appointment: z.object({
    startAt: z.date().nullable().optional(),
    endAt: z.date().nullable().optional(),
  }),
  tags: z.array(z.string()).optional(),
  isWaiter: z.boolean().optional(),
  customer: z
    .object({
      schemaVersion: z.number().int(),
      id: z.string(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      contactFirstName: z.string().optional(),
      contactLastName: z.string().optional(),
      name: z.string().optional(),
      phone: z
        .array(
          z.object({
            number: z
              .string()
              .regex(PHONE_NUMBER_REGEX, "Invalid phone format"),
            type: z.string().optional(),
            primary: z.boolean().optional(),
          })
        )
        .optional(),
    })
    .optional(),
  vehicle: z
    .object({
      schemaVersion: z.number().int(),
      id: z.string(),
      year: z
        .number()
        .min(1900, "Year must be after 1900")
        .max(
          new Date().getFullYear() + 2,
          "Year cannot be more than 2 years in the future"
        )
        .optional()
        .nullable(),
      make: z.string().optional(),
      model: z.string().optional(),
    })
    .optional(),
  laborSales: z
    .number()
    .min(0, "Labor sales must be positive")
    .optional()
    .nullable(),
  partsSales: z
    .number()
    .min(0, "Parts sales must be positive")
    .optional()
    .nullable(),
  completedDate: z.date().optional().nullable(),
  ...shape,
});

// eslint-disable-next-line no-unused-vars
const defaultValues: { [key in CardFieldKeys]?: string } & Pick<
  Card,
  | "totalLaborHours"
  | "promisedTime"
  | "dropOffTime"
  | "appointment"
  | "tags"
  | "isWaiter"
  | "customer"
  | "laborSales"
  | "partsSales"
  | "completedDate"
  | "vehicle"
  | "keyTag"
  | "serviceWriterId"
  | "technicianId"
  | "source"
  | "schemaVersion"
  | "cardNumber"
> = {
  totalLaborHours: {
    estimatedLabor: 0,
    loggedLabor: 0,
  },
  promisedTime: null,
  dropOffTime: null,
  appointment: {
    startAt: null,
    endAt: null,
  },
  tags: [],
  isWaiter: false,
  customer: {
    schemaVersion: 2,
    id: "",
    firstName: "",
    lastName: "",
    phone: [],
  },
  vehicle: {
    schemaVersion: 2,
    id: "",
    year: null,
    make: "",
    model: "",
  },
  laborSales: null,
  partsSales: null,
  completedDate: null,
  keyTag: "",
  serviceWriterId: "none",
  technicianId: "none",
  source: "manual",
  schemaVersion: 2,
  cardNumber: "",
};
CARD_FIELDS.forEach((field) => {
  defaultValues[field.key] = "";
});

export interface CardFormProps<T> {
  cardData?: PatchCardDto;
  /**
   * Prefill values for a NEW card (duplicate-mode), kept distinct from
   * `cardData` (edit mode) so `isCreating` stays accurate — never pass
   * both. See buildDuplicateCardPrefill (WEBV3-300) for the one producer
   * of this shape today.
   */
  initialValues?: CreateCardDto;
  onSubmit: (values: T) => Promise<void>;
  onCancel?: () => void;
}

export const CardForm = <T,>({
  cardData,
  initialValues,
  onSubmit,
  onCancel,
}: CardFormProps<T>) => {
  const { terminolize, terminolizeAuto } = useTerminologyReplacer();
  const { shop, tags } = useStore((s) => s);
  const serviceAdvisors = useServiceAdvisors();
  const technicians = useTechnicians();
  const user = useStore((s) => s.user);
  const isDuplicating = !cardData && !!initialValues;
  const prefillSource: PatchCardDto | CreateCardDto | undefined =
    cardData ?? initialValues;

  // If the user is a service advisor or technician, set them as the default selected value
  const userIsServiceAdvisor = serviceAdvisors?.some(
    (sa) => sa.shopHeroAuthUid === user?.id && !sa.hideInShopHero
  );
  const userIsTechnician = technicians?.some(
    (tech) => tech.shopHeroAuthUid === user?.id && !tech.hideInShopHero
  );

  if (userIsServiceAdvisor && !prefillSource?.serviceWriterId) {
    defaultValues.serviceWriterId =
      serviceAdvisors?.find((sa) => sa.shopHeroAuthUid === user?.id)?.id ??
      "none";
  }
  if (userIsTechnician && !prefillSource?.technicianId) {
    defaultValues.technicianId =
      technicians?.find((tech) => tech.shopHeroAuthUid === user?.id)?.id ??
      "none";
  }

  // Get required vehicle fields from shop config
  const requiredVehicleFields = useMemo(
    () =>
      shop?.cardConfig?.cardContents?.requiredVehicleFields || VEHICLE_FIELDS,
    [shop?.cardConfig?.cardContents?.requiredVehicleFields]
  );

  // Create dynamic schema with conditional vehicle field validation
  const dynamicFormSchema = useMemo(() => {
    let schema = formSchema;

    if (shop?.cardConfig?.cardContents?.manualVehicleInfoEnabled) {
      // Add refinements for each required vehicle field
      requiredVehicleFields.forEach((field) => {
        // @ts-ignore
        schema = schema.refine(
          (data) => {
            const value = data.vehicle?.[field];
            if (field === "year") {
              return value !== null && value !== undefined;
            } else {
              return value !== "" && value !== undefined;
            }
          },
          {
            message: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
            path: [`vehicle.${field}`],
          }
        );
      });
    }

    return schema;
  }, [
    requiredVehicleFields,
    shop?.cardConfig?.cardContents?.manualVehicleInfoEnabled,
  ]);

  // Auto-increment functionality for card numbers
  const isCreating = !cardData;
  const autoIncrement = useCardNumberAutoIncrement({
    isCreating,
    initialCardNumber: prefillSource?.cardNumber || "",
  });

  // Apply migration logic for backwards compatibility
  const migrationData = useMemo(() => {
    if (cardData && shop) {
      return migrateCardFieldData(cardData, shop);
    }
    return {};
  }, [cardData, shop]);

  // Determine which legacy fields should be hidden due to successful migration.
  // Fields configured as "Custom" stay visible either way, so a card created
  // with them blank can still have them filled in later.
  const hiddenLegacyFields = useMemo(
    () => resolveHiddenLegacyFields(migrationData, cardData, shop),
    [migrationData, cardData, shop]
  );

  const hasLegacyFields = useMemo(
    () =>
      CARD_FIELDS.filter((cardField) =>
        !shop?.cardConfig?.cardContents
          ? false
          : shop?.cardConfig.cardContents[cardField.key] !== "" &&
            shop?.cardConfig.cardContents[cardField.key] !== "RO Number" &&
            shop?.cardConfig.cardContents[cardField.key] !== "Key Tag" &&
            !hiddenLegacyFields.has(cardField.key)
      ).length > 0,
    [hiddenLegacyFields, shop?.cardConfig.cardContents]
  );

  const formDefaultValues = {
    cardNumber: isCreating
      ? autoIncrement.getInitialCardNumber()
      : cardData?.cardNumber || "",
    serviceWriterId: "none",
    technicianId: "none",
    ...defaultValues,
    ...migrationData,
    ...prefillSource,
    appointment: {
      startAt:
        firestoreToDateTime(prefillSource?.appointment?.startAt) ??
        defaultValues.appointment?.startAt,
      endAt:
        firestoreToDateTime(prefillSource?.appointment?.endAt) ??
        defaultValues.appointment?.endAt,
    },
    promisedTime:
      firestoreToDateTime(prefillSource?.promisedTime) ??
      defaultValues.promisedTime,
    dropOffTime:
      firestoreToDateTime(prefillSource?.dropOffTime) ??
      defaultValues.dropOffTime,
    customer: {
      schemaVersion: 2,
      id: prefillSource?.customer?.id || shortUUID.generate(),
      firstName:
        prefillSource?.customer?.firstName ||
        migrationData.customer?.firstName ||
        "",
      lastName:
        prefillSource?.customer?.lastName ||
        migrationData.customer?.lastName ||
        "",
      phone: normalizePhoneForForm(
        prefillSource?.customer?.phone || migrationData.customer?.phone
      ),
    },
    vehicle: {
      schemaVersion: 2,
      id: prefillSource?.vehicle?.id || shortUUID.generate(),
      year: prefillSource?.vehicle?.year
        ? Number(prefillSource.vehicle.year)
        : migrationData.vehicle?.year || null,
      make: prefillSource?.vehicle?.make || migrationData.vehicle?.make || "",
      model:
        prefillSource?.vehicle?.model || migrationData.vehicle?.model || "",
    },
    laborSales: prefillSource?.laborSales ?? defaultValues.laborSales,
    partsSales: prefillSource?.partsSales ?? defaultValues.partsSales,
    completedDate:
      firestoreToDateTime(prefillSource?.completedDate) ??
      defaultValues.completedDate,
    // `...prefillSource` above carries the card's own tag insertion order; every
    // tag surface is meant to follow the shop's shared order instead.
    tags: sortTagsByShopOrder(prefillSource?.tags ?? [], tags?.tags),
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(dynamicFormSchema),
    defaultValues: formDefaultValues,
  });

  // Update cardNumber field when auto-increment suggestion loads
  React.useEffect(() => {
    if (
      isCreating &&
      autoIncrement.isAutoSuggested &&
      autoIncrement.suggestedCardNumber
    ) {
      const currentValue = form.getValues("cardNumber");
      // Only update if the current value is empty or is the previously suggested value
      if (
        !currentValue ||
        currentValue === "" ||
        !autoIncrement.hasUserOverridden
      ) {
        form.setValue("cardNumber", autoIncrement.suggestedCardNumber, {
          shouldValidate: true,
        });
      }
    }
  }, [
    isCreating,
    autoIncrement.isAutoSuggested,
    autoIncrement.suggestedCardNumber,
    autoIncrement.hasUserOverridden,
    form,
  ]);

  // Duplicate mode's "-D"-suffixed cardNumber must survive a shop's
  // auto-increment suggestion (WEBV3-300). Calling the hook's own public
  // handleCardNumberChange marks it as user-overridden in the same effect
  // pass as the hook's internal "is auto-increment enabled" check, before
  // that check's own state update can trigger a fetch — see this plan's
  // Task 3 for the full timing argument.
  React.useEffect(() => {
    if (isDuplicating && initialValues?.cardNumber) {
      autoIncrement.handleCardNumberChange(initialValues.cardNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watch = form.watch;

  const cardContents = useMemo(
    () => (shop ? getCardContents(shop) : null),
    [shop]
  );

  const fieldEnabled = useCallback(
    (field: ManualCardContents) => cardContents?.[field] ?? false,
    [cardContents]
  );

  const isCardContent = useCallback(
    (key: CardFieldKey) => {
      if (shop?.cardConfig?.cardContents?.primaryField === key) {
        return {
          field: "primaryField",
          isCardContent: true,
        };
      } else if (shop?.cardConfig?.cardContents?.secondaryField === key) {
        return {
          field: "secondaryField",
          isCardContent: true,
        };
      } else if (shop?.cardConfig?.cardContents?.tertiaryField === key) {
        return {
          field: "tertiaryField",
          isCardContent: true,
        };
      }
      return false;
    },
    [shop?.cardConfig?.cardContents]
  );

  // Determine which sections are required (always shown)
  const isSectionRequired = useCallback(
    (section: FieldSection): boolean => {
      switch (section) {
        case "customer":
          return (
            !!isCardContent("Customer Last Name") ||
            !!isCardContent("Customer Name") ||
            !!isCardContent("Phone Number")
          );
        case "vehicle":
          return (
            !!isCardContent("Year, Make, Model of vehicle") ||
            !!isCardContent("YY Model") ||
            (fieldEnabled("manualVehicleInfoEnabled") &&
              requiredVehicleFields.length > 0)
          );
        case "appointments":
          return false;
        case "employeeAndJob":
          return false;
        case "financial":
          return false;
        case "miscellaneous":
          return false; // Always required
        default:
          return false;
      }
    },
    [isCardContent, fieldEnabled, requiredVehicleFields]
  );

  // Determine which sections are available (enabled in config)
  const availableSections = useMemo((): FieldSection[] => {
    const sections: FieldSection[] = [];

    // Customer section
    if (
      fieldEnabled("manualCustomerInfoEnabled") ||
      isCardContent("Customer Last Name") ||
      isCardContent("Customer Name") ||
      isCardContent("Phone Number")
    ) {
      sections.push("customer");
    }

    // Vehicle section
    if (
      fieldEnabled("manualVehicleInfoEnabled") ||
      isCardContent("Year, Make, Model of vehicle") ||
      isCardContent("YY Model")
    ) {
      sections.push("vehicle");
    }

    // Appointments section
    if (
      fieldEnabled("manualDueInTimeEnabled") ||
      fieldEnabled("manualDueOutTimeEnabled") ||
      fieldEnabled("manualAppointmentEnabled") ||
      fieldEnabled("manualWaiterEnabled")
    ) {
      sections.push("appointments");
    }

    // Employee and Job section
    if (
      fieldEnabled("manualServiceAdvisorEnabled") ||
      fieldEnabled("manualTechnicianEnabled") ||
      fieldEnabled("manualJobHoursEnabled")
    ) {
      sections.push("employeeAndJob");
    }

    // Financial section
    if (fieldEnabled("manualFinancialFieldsEnabled")) {
      sections.push("financial");
    }

    // Miscellaneous section
    if (
      fieldEnabled("manualTagsEnabled") ||
      fieldEnabled("manualMarkCompleteEnabled")
    ) {
      sections.push("miscellaneous");
    }

    return sections;
  }, [fieldEnabled, isCardContent]);

  // Helper to check if a section has data in cardData
  const sectionHasData = useCallback(
    (
      section: FieldSection,
      values: Partial<Card> | undefined = cardData
    ): boolean => {
      if (!values) return false;

      switch (section) {
        case "customer":
          return !!(
            values.customer?.firstName ||
            values.customer?.lastName ||
            values.customer?.name ||
            values.customer?.phone?.some((p) => p.number)
          );
        case "vehicle":
          return !!(
            values.vehicle?.year ||
            values.vehicle?.make ||
            values.vehicle?.model
          );
        case "appointments":
          return !!(
            values.appointment?.startAt ||
            values.appointment?.endAt ||
            values.promisedTime ||
            values.dropOffTime
          );
        case "employeeAndJob":
          return !!(
            values.serviceWriterId ||
            values.technicianId ||
            values.totalLaborHours?.estimatedLabor ||
            values.totalLaborHours?.loggedLabor
          );
        case "financial":
          return !!(values.laborSales || values.partsSales);
        case "miscellaneous":
          return !!(
            (values.tags && values.tags.length > 0) ||
            values.completedDate
          );
        default:
          return false;
      }
    },
    [cardData]
  );

  // State to track which sections are visible
  const [visibleSections, setVisibleSections] = React.useState<
    Set<FieldSection>
  >(() => {
    // Initialize with required sections and sections with data
    const initial = new Set<FieldSection>();
    availableSections.forEach((section) => {
      if (
        isSectionRequired(section) ||
        sectionHasData(section, form.getValues())
      ) {
        initial.add(section);
      }
    });
    return initial;
  });

  // Handler to add a section
  const handleAddSection = useCallback((section: FieldSection) => {
    setVisibleSections((prev) => new Set(prev).add(section));
  }, []);

  // Handler to remove a section
  const handleRemoveSection = useCallback((section: FieldSection) => {
    setVisibleSections((prev) => {
      const next = new Set(prev);
      next.delete(section);
      return next;
    });
  }, []);

  // Helper to check if a section should be displayed
  const isSectionVisible = useCallback(
    (section: FieldSection) => {
      return isSectionRequired(section) || visibleSections.has(section);
    },
    [isSectionRequired, visibleSections]
  );

  if (!shop || !cardContents) return null;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (data) => {
          await onSubmit(data as T);
          // Update the counter after successful submission for new cards
          if (isCreating && data.cardNumber) {
            await autoIncrement.updateCounter(data.cardNumber);
          }
        })}
        className="space-y-1"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
          <FormField
            control={form.control}
            name="cardNumber"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {terminolize(
                    "Repair Order Number",
                    TerminologyOptions.repairOrderNumber,
                    "singular"
                  )}
                  {autoIncrement.isAutoSuggested && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      (Auto-suggested)
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      disabled={
                        form.formState.isSubmitting ||
                        autoIncrement.isFetchingSuggestion
                      }
                      placeholder={
                        autoIncrement.isFetchingSuggestion
                          ? "Loading..."
                          : "0001"
                      }
                      data-form-type="other"
                      type="text"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        autoIncrement.handleCardNumberChange(e.target.value);
                        CARD_FIELDS.forEach((cardField) => {
                          if (cardContents[cardField.key] == "RO Number") {
                            form.setValue(cardField.key, e.target.value, {
                              shouldValidate: true,
                            });
                          }
                        });
                      }}
                      className={cn(
                        "max-w-64 text-primary",
                        autoIncrement.isAutoSuggested
                          ? "border-green-300 bg-green-50"
                          : ""
                      )}
                    />
                    {autoIncrement.isFetchingSuggestion && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                </FormControl>
                {autoIncrement.shouldShowSuggestion &&
                  autoIncrement.suggestedCardNumber && (
                    <p className="text-green-600 text-xs">
                      💡 Next suggested number:{" "}
                      {autoIncrement.suggestedCardNumber}
                    </p>
                  )}
                <FormMessage />
              </FormItem>
            )}
          />
          {fieldEnabled("manualMarkCompleteEnabled") && (
            <div className="flex flex-col gap-1 sm:pt-8">
              <Button
                type="button"
                variant={watch("completedDate") ? "default" : "outline"}
                size="sm"
                disabled={
                  form.formState.isSubmitting || !form.formState.isValid
                }
                onClick={() => {
                  const currentCompletedDate = form.getValues("completedDate");
                  if (!currentCompletedDate) {
                    form.setValue("completedDate", new Date(), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                className="whitespace-nowrap"
              >
                <Icon
                  name={
                    watch("completedDate") ? IconNames.check : IconNames.circle
                  }
                  size={14}
                  className="mr-1"
                />
                {watch("completedDate")
                  ? "Marked Complete"
                  : "Mark as Complete"}
              </Button>
              {watch("completedDate") && (
                <p className="text-xs text-muted-foreground">
                  {new Date(watch("completedDate") as Date).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Field Section Manager */}
        <FieldSectionManager
          availableSections={availableSections}
          visibleSections={visibleSections}
          onAddSection={handleAddSection}
        />

        <div className="flex flex-col gap-2">
          {(isCardContent("Key Tag") || isCardContent("RO Number - Tag")) && (
            <FormField
              control={form.control}
              name="keyTag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Key Tag</FormLabel>
                  <FormControl>
                    <Input
                      disabled={form.formState.isSubmitting}
                      placeholder="Enter key tag"
                      data-form-type="other"
                      type="text"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        CARD_FIELDS.forEach((cardField) => {
                          if (cardContents[cardField.key] == "Key Tag") {
                            form.setValue(cardField.key, e.target.value, {
                              shouldValidate: true,
                            });
                          }
                        });
                      }}
                      className="text-primary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {hasLegacyFields && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <InputFields
                fields={CARD_FIELDS.filter(
                  (cardField) =>
                    cardContents[cardField.key] !== "" &&
                    cardContents[cardField.key] !== "RO Number" &&
                    cardContents[cardField.key] !== "Key Tag" &&
                    !hiddenLegacyFields.has(cardField.key)
                ).map((cardField) => {
                  const fieldKey = cardContents[cardField.key];
                  const isCustom = fieldKey === "Custom";
                  const customLabel = isCustom
                    ? cardContents[`${cardField.key}Label`]
                    : null;
                  const label =
                    isCustom && customLabel
                      ? customLabel
                      : terminolizeAuto(
                          `${fieldKey === "" ? "Primary Label" : fieldKey}`
                        );

                  return {
                    name: cardField.key,
                    type: "text" as const,
                    label,
                    placeholder: `Enter the ${label.toLowerCase()}`,
                    disabled: form.formState.isSubmitting,
                  } as FieldType<z.infer<typeof formSchema>>;
                })}
                form={form}
              />
            </div>
          )}

          {/* Customer Information Section */}
          {isSectionVisible("customer") &&
            (fieldEnabled("manualCustomerInfoEnabled") ||
              isCardContent("Customer Last Name") ||
              isCardContent("Customer Name") ||
              isCardContent("Phone Number")) && (
              <div className="space-y-1 rounded-md border p-3 text-sm shadow-lg">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold">Customer</h3>
                  {!isSectionRequired("customer") &&
                    !sectionHasData("customer", form.getValues()) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSection("customer")}
                        className="h-6 w-6 p-0"
                      >
                        <Icon name={IconNames.x} className="h-4 w-4" />
                      </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InputFields
                    fields={[
                      {
                        name: "customer.firstName" as const,
                        type: "text" as const,
                        label: "First Name",
                        placeholder: "Enter first name",
                        disabled: form.formState.isSubmitting,
                      } as FieldType<z.infer<typeof formSchema>>,
                      {
                        name: "customer.lastName" as const,
                        type: "text" as const,
                        label: "Last Name",
                        placeholder: "Enter last name",
                        disabled: form.formState.isSubmitting,
                      } as FieldType<z.infer<typeof formSchema>>,
                    ]}
                    form={form}
                  />
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <FormField
                    control={form.control}
                    name="customer.phone"
                    render={({ field }) => {
                      const currentPhone = field.value?.[0] || {
                        number: "",
                        type: "mobile",
                        primary: true,
                      };
                      const [phoneType, setPhoneType] = React.useState(
                        currentPhone.type || "mobile"
                      );

                      return (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <div className="space-y-2">
                              <div className="flex overflow-hidden rounded-md border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                <Select
                                  value={phoneType}
                                  disabled={form.formState.isSubmitting}
                                  onValueChange={(newType) => {
                                    setPhoneType(newType);
                                    const phoneArray = field.value || [];
                                    phoneArray[0] = {
                                      ...currentPhone,
                                      type: newType as
                                        | "mobile"
                                        | "home"
                                        | "work",
                                    };
                                    field.onChange(phoneArray);
                                  }}
                                >
                                  <SelectTrigger className="w-28 rounded-none border-0 border-r text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="mobile">
                                      Mobile
                                    </SelectItem>
                                    <SelectItem value="home">Home</SelectItem>
                                    <SelectItem value="work">Work</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  containerClassName={"flex-1 z-10"}
                                  className="flex-1 flex-grow border-0 text-primary focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                  type="tel"
                                  placeholder="(555) 123-4567"
                                  disabled={form.formState.isSubmitting}
                                  value={currentPhone.number || ""}
                                  onChange={(e) => {
                                    const phoneNumber = e.target.value;
                                    // Format phone number
                                    const digits = phoneNumber.replace(
                                      /\D/g,
                                      ""
                                    );
                                    let formatted = "";
                                    if (digits.length <= 3) {
                                      formatted = `(${digits}`;
                                    } else if (digits.length <= 6) {
                                      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                                    } else {
                                      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
                                    }

                                    const phoneArray = field.value || [];
                                    phoneArray[0] = {
                                      number: formatted,
                                      type: phoneType as
                                        | "mobile"
                                        | "home"
                                        | "work",
                                      primary: true,
                                    };
                                    field.onChange(phoneArray);
                                  }}
                                />
                              </div>
                            </div>
                          </FormControl>
                          <PhoneNumberErrorMessage />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>
            )}

          {/* Vehicle Information Section */}
          {isSectionVisible("vehicle") &&
            (fieldEnabled("manualVehicleInfoEnabled") ||
              isCardContent("Year, Make, Model of vehicle") ||
              isCardContent("YY Model")) && (
              <div className="space-y-1 rounded-md border p-3 text-sm shadow-lg">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold">
                    {terminolize(
                      "Vehicle",
                      TerminologyOptions.vehicle,
                      "singular"
                    )}
                  </h3>
                  {!isSectionRequired("vehicle") &&
                    !sectionHasData("vehicle", form.getValues()) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSection("vehicle")}
                        className="h-6 w-6 p-0"
                      >
                        <Icon name={IconNames.x} className="h-4 w-4" />
                      </Button>
                    )}
                </div>
                <div className="flex flex-grow flex-col gap-2 sm:flex-row">
                  <InputFields
                    className={"flex-grow"}
                    fields={
                      [
                        requiredVehicleFields.includes("year") && {
                          name: "vehicle.year" as const,
                          type: "number" as const,
                          label: "Year",
                          placeholder: "Enter year",
                          min: 1900,
                          max: new Date().getFullYear() + 2,
                          disabled: form.formState.isSubmitting,
                        },
                        requiredVehicleFields.includes("make") && {
                          name: "vehicle.make" as const,
                          type: "text" as const,
                          label: "Make",
                          placeholder: "Enter make",
                          disabled: form.formState.isSubmitting,
                        },
                        requiredVehicleFields.includes("model") && {
                          name: "vehicle.model" as const,
                          type: "text" as const,
                          label: "Model",
                          placeholder: "Enter model",
                          disabled: form.formState.isSubmitting,
                        },
                      ].filter(Boolean) as FieldType<
                        z.infer<typeof formSchema>
                      >[]
                    }
                    form={form}
                  />
                </div>
              </div>
            )}

          {isSectionVisible("appointments") &&
            (fieldEnabled("manualDueInTimeEnabled") ||
              fieldEnabled("manualDueOutTimeEnabled") ||
              fieldEnabled("manualAppointmentEnabled") ||
              fieldEnabled("manualWaiterEnabled")) && (
              <div className="space-y-1 rounded-md border p-3 text-sm shadow-lg">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold">Appointment</h3>
                  {!isSectionRequired("appointments") &&
                    !sectionHasData("appointments", form.getValues()) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSection("appointments")}
                        className="h-6 w-6 p-0"
                      >
                        <Icon name={IconNames.x} className="h-4 w-4" />
                      </Button>
                    )}
                </div>
                <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2">
                  {fieldEnabled("manualDueInTimeEnabled") ? (
                    <Fragment>
                      <InputFields
                        fields={[
                          {
                            name: "dropOffTime" as const,
                            type: "datetime-local" as const,
                            label: "Drop Off Time",
                            disabled: form.formState.isSubmitting,
                          } as FieldType<z.infer<typeof formSchema>>,
                        ]}
                        form={form}
                      />
                    </Fragment>
                  ) : null}
                  {fieldEnabled("manualDueOutTimeEnabled") ? (
                    <Fragment>
                      <InputFields
                        fields={[
                          {
                            name: "promisedTime" as const,
                            type: "datetime-local" as const,
                            label: "Promised Time",
                            disabled: form.formState.isSubmitting,
                          } as FieldType<z.infer<typeof formSchema>>,
                        ]}
                        form={form}
                      />
                    </Fragment>
                  ) : null}
                  {fieldEnabled("manualAppointmentEnabled") ? (
                    <Fragment>
                      <InputFields
                        fields={[
                          {
                            name: "appointment.startAt" as const,
                            type: "datetime-local" as const,
                            label: "Appointment Start Time",
                            disabled: form.formState.isSubmitting,
                          } as FieldType<z.infer<typeof formSchema>>,
                          {
                            name: "appointment.endAt" as const,
                            type: "datetime-local" as const,
                            label: "Appointment End Time",
                            disabled: form.formState.isSubmitting,
                          } as FieldType<z.infer<typeof formSchema>>,
                        ]}
                        form={form}
                      />
                    </Fragment>
                  ) : null}

                  {fieldEnabled("manualWaiterEnabled") ? (
                    <SwitchField
                      name={"isWaiter"}
                      label={"Waiter"}
                      disabled={form.formState.isSubmitting}
                    />
                  ) : null}
                </div>
              </div>
            )}

          {/* Add Service Advisor Select */}
          {isSectionVisible("employeeAndJob") &&
            (fieldEnabled("manualServiceAdvisorEnabled") ||
              fieldEnabled("manualTechnicianEnabled") ||
              fieldEnabled("manualJobHoursEnabled")) && (
              <div className="space-y-1 rounded-md border p-3 text-sm shadow-lg">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold">Employee and Job</h3>
                  {!isSectionRequired("employeeAndJob") &&
                    !sectionHasData("employeeAndJob", form.getValues()) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSection("employeeAndJob")}
                        className="h-6 w-6 p-0"
                      >
                        <Icon name={IconNames.x} className="h-4 w-4" />
                      </Button>
                    )}
                </div>
                <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2">
                  {fieldEnabled("manualServiceAdvisorEnabled") ? (
                    <FormField
                      control={form.control}
                      name="serviceWriterId" // Make sure this name matches the field in your form
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {terminolize(
                              "Service Advisor",
                              TerminologyOptions.serviceAdvisor,
                              "singular"
                            )}
                          </FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value); // Update form field value
                              }}
                              value={field.value} // Ensure the correct value is displayed
                              disabled={form.formState.isSubmitting}
                            >
                              <SelectTrigger className="flex-grow">
                                <SelectValue
                                  placeholder={terminolize(
                                    "Service Advisor",
                                    TerminologyOptions.serviceAdvisor,
                                    "singular"
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  No{" "}
                                  {terminolize(
                                    "Service Advisor",
                                    TerminologyOptions.serviceAdvisor,
                                    "singular"
                                  )}
                                </SelectItem>
                                {serviceAdvisors
                                  ?.filter((sa) => !sa.hideInShopHero)
                                  .map((serviceAdvisor) => (
                                    <SelectItem
                                      key={serviceAdvisor.id}
                                      value={`${serviceAdvisor.id}`}
                                    >
                                      {/* Circle color next to the name */}
                                      <span
                                        className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                                        style={{
                                          backgroundColor:
                                            getServiceAdvisorColor(
                                              serviceAdvisor.id,
                                              serviceAdvisors || []
                                            ),
                                        }}
                                      ></span>
                                      {serviceAdvisor.firstName}{" "}
                                      {serviceAdvisor.lastName}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}

                  {/* Add Technician Select */}

                  {fieldEnabled("manualTechnicianEnabled") ? (
                    <FormField
                      control={form.control}
                      name="technicianId" // Make sure this name matches the field in your form
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {terminolize(
                              "Technician",
                              TerminologyOptions.technician,
                              "singular"
                            )}
                          </FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value); // Update form field value
                              }}
                              value={field.value} // Ensure the correct value is displayed
                              disabled={form.formState.isSubmitting}
                            >
                              <SelectTrigger className="flex-grow">
                                <SelectValue
                                  placeholder={terminolize(
                                    "Technician",
                                    TerminologyOptions.technician,
                                    "singular"
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  No{" "}
                                  {terminolize(
                                    "Technician",
                                    TerminologyOptions.technician,
                                    "singular"
                                  )}
                                </SelectItem>
                                {technicians
                                  ?.filter((tech) => !tech.hideInShopHero)
                                  .map((technician) => (
                                    <SelectItem
                                      key={technician.id}
                                      value={`${technician.id}`}
                                    >
                                      {technician.firstName}{" "}
                                      {technician.lastName}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                  {fieldEnabled("manualJobHoursEnabled") && (
                    <InputFields
                      fields={[
                        {
                          name: "totalLaborHours.estimatedLabor" as const,
                          type: "number" as const,
                          label: "Total Labor Hours",
                          placeholder: "Enter total hours",
                          disabled: form.formState.isSubmitting,
                        },
                        {
                          name: "totalLaborHours.loggedLabor" as const,
                          type: "number" as const,
                          label: "Completed Labor Hours",
                          placeholder: "Enter completed hours",
                          disabled: form.formState.isSubmitting,
                        },
                      ]}
                      form={form}
                    />
                  )}
                </div>
              </div>
            )}

          {/* Financial Information Section */}
          {isSectionVisible("financial") &&
            fieldEnabled("manualFinancialFieldsEnabled") && (
              <div className="space-y-1 rounded-md border p-3 text-sm shadow-lg">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold">
                    Financial Information
                  </h3>
                  {!isSectionRequired("financial") &&
                    !sectionHasData("financial", form.getValues()) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSection("financial")}
                        className="h-6 w-6 p-0"
                      >
                        <Icon name={IconNames.x} className="h-4 w-4" />
                      </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InputFields
                    fields={[
                      {
                        name: "laborSales" as const,
                        type: "currency" as const,
                        label: "Labor Sales",
                        placeholder: "Enter labor sales amount",
                      } as FieldType<z.infer<typeof formSchema>>,
                      {
                        name: "partsSales" as const,
                        type: "currency" as const,
                        label: "Parts Sales",
                        placeholder: "Enter parts sales amount",
                      } as FieldType<z.infer<typeof formSchema>>,
                    ]}
                    form={form}
                  />
                  <div className="col-span-1 sm:col-span-2">
                    <p className="text-base text-muted-foreground">
                      Sales Total: $
                      {(
                        ((watch("laborSales") || 0) +
                          (watch("partsSales") || 0)) /
                        100
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {isSectionVisible("miscellaneous") && (
            <div className="space-y-1 rounded-md border p-3 text-sm shadow-lg">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="text-lg font-semibold">Miscellaneous</h3>
                {!isSectionRequired("miscellaneous") &&
                  !sectionHasData("miscellaneous", form.getValues()) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSection("miscellaneous")}
                      className="h-6 w-6 p-0"
                    >
                      <Icon name={IconNames.x} className="h-4 w-4" />
                    </Button>
                  )}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <InputFields
                  fields={[
                    {
                      name: "completedDate" as const,
                      type: "datetime-local" as const,
                      label: "Completed Date",
                      disabled: form.formState.isSubmitting,
                    } as FieldType<z.infer<typeof formSchema>>,
                  ]}
                  form={form}
                />
                {fieldEnabled("manualTagsEnabled") ? (
                  <div className="col-span-2">
                    <TagsField
                      form={form}
                      name={"tags"}
                      label={"Tags"}
                      placeholder={"Enter tags to be associated with this card"}
                      suggestedTags={tags?.tags?.map((t) => t.label)}
                      maxTags={20}
                      maxSuggestedTags={
                        SHOW_ALL_SUGGESTED_TAGS ? undefined : 20
                      }
                      allTags={tags?.tags}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row justify-end gap-4">
          {onCancel ? (
            <Button
              variant="secondary"
              type="button"
              disabled={form.formState.isSubmitting}
              onClick={onCancel}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            variant="default"
            type="submit"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
